import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// ====== Import schemas from generated ======
const {
  StoreFirestoreResponse,
  CreateStoreDto,
  UpdateStoreDto,
  TimezoneDto,
  TimeDto,
  FiscalOperatorDto,
  WiponCredentialsDto,
  DeleteResponseDto,
} = schemas;

// ====== Comparison Schema (not in generated for single store) ======
export const StoreComparisonResultSchema = z.object({
  exists: z.boolean(),
  supabaseData: StoreFirestoreResponse.nullable(),
}).passthrough();

// ====== Re-export schemas for external use ======
export const StoreResponseSchema = StoreFirestoreResponse;
export const CreateStoreSchema = CreateStoreDto;
export const UpdateStoreSchema = UpdateStoreDto;
export const TimezoneSchema = TimezoneDto;
export const TimeSchema = TimeDto;
export const FiscalOperatorSchema = FiscalOperatorDto;
export const WiponCredentialsSchema = WiponCredentialsDto;
export const DeleteResponseSchema = DeleteResponseDto;

// ====== Type Exports ======
export type StoreResponse = z.infer<typeof StoreFirestoreResponse>;
export type CreateStore = z.infer<typeof CreateStoreDto>;
export type UpdateStore = z.infer<typeof UpdateStoreDto>;
export type Time = z.infer<typeof TimeDto>;
export type StoreComparisonResult = z.infer<typeof StoreComparisonResultSchema>;

/**
 * Create stores API factory
 */
export function createStoresApi(client: TypedApiClient) {
  return {
    /**
     * Get all stores for the organization
     */
    getStores: (): Promise<StoreResponse[]> => {
      return client.get('/stores', {
        responseSchema: z.array(StoreFirestoreResponse),
      });
    },

    /**
     * Get a single store by ID
     */
    getStoreById: (id: string): Promise<StoreResponse> => {
      return client.get(apiPath`/stores/${id}`, {
        responseSchema: StoreFirestoreResponse,
      });
    },

    /**
     * Create a new store
     */
    createStore: (data: CreateStore): Promise<StoreResponse> => {
      return client.post('/stores', {
        data,
        requestSchema: CreateStoreDto,
        responseSchema: StoreFirestoreResponse,
      });
    },

    /**
     * Update an existing store
     */
    updateStore: (id: string, data: UpdateStore): Promise<StoreResponse> => {
      return client.patch(apiPath`/stores/${id}`, {
        data,
        requestSchema: UpdateStoreDto,
        responseSchema: StoreFirestoreResponse,
      });
    },

    /**
     * Delete a store
     */
    deleteStore: (id: string): Promise<z.infer<typeof DeleteResponseDto>> => {
      return client.delete(apiPath`/stores/${id}`, {
        responseSchema: DeleteResponseDto,
      });
    },

    /**
     * Compare store with Firestore data
     */
    compareWithFirestore: (firestoreId: string): Promise<StoreComparisonResult> => {
      return client.get(apiPath`/stores/compare/${firestoreId}`, {
        responseSchema: StoreComparisonResultSchema,
      });
    },
  };
}
